import React from "react";
import { Link } from "react-router-dom";

export default function ResearchTips() {
  return (
    <section className="relative bg-[#02042b] text-white mt-10 sm:mt-12">

      {/* subtle glow accents */}
      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          background:
            "radial-gradient(900px 400px at 10% -10%, rgba(99,102,241,.18), transparent 60%), radial-gradient(800px 360px at 120% 0%, rgba(236,72,153,.15), transparent 55%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">

        {/* Header */}
        <header className="text-center max-w-3xl mx-auto mb-8 sm:mb-10">
          <h1 className="text-2xl sm:text-4xl font-extrabold leading-tight">
            JSTOR Research Tips for Humanities and Social Sciences
          </h1>

          <p className="mt-3 text-gray-300 text-sm sm:text-base leading-relaxed">
            Discover JSTOR research tips to access scholarly articles, manage citations,
            evaluate credible journals, and boost academic writing across humanities and
            social sciences.
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">

          {/* Sidebar */}
          <aside className="lg:col-span-4 order-2 lg:order-1">
            <div className="lg:sticky lg:top-24 bg-white/5 backdrop-blur rounded-xl border border-white/10 p-4 sm:p-5">
              <h2 className="text-base sm:text-lg font-semibold mb-3">
                Table of Contents
              </h2>

              <nav className="text-sm text-gray-300 space-y-2">
                <a href="#intro" className="block hover:text-white">Introduction</a>
                <a href="#points" className="block hover:text-white">5 Key Points</a>
                <a href="#value" className="block hover:text-white">Understanding JSTOR</a>
                <a href="#search" className="block hover:text-white">Search Strategies</a>
                <a href="#credibility" className="block hover:text-white">Evaluating Sources</a>
                <a href="#citations" className="block hover:text-white">Managing Citations</a>
                <a href="#thesis" className="block hover:text-white">Using JSTOR for Thesis</a>
                <a href="#openaccess" className="block hover:text-white">Open Access Resources</a>
                <a href="#tools" className="block hover:text-white">Interface & Tools</a>
                <a href="#support" className="block hover:text-white">How We Help</a>
                <a href="#faqs" className="block hover:text-white">FAQs</a>
                <a href="#conclusion" className="block hover:text-white">Conclusion</a>
              </nav>
            </div>
          </aside>

          {/* Article */}
          <article className="lg:col-span-8 space-y-8 sm:space-y-10 order-1 lg:order-2">

            {/* Intro */}
            <section id="intro" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-5 sm:p-6">
              <p className="text-gray-200 leading-relaxed">
                JSTOR research tips for humanities and social sciences are essential for students
                and researchers looking to access credible, high-quality academic resources efficiently.
              </p>

              <p className="text-gray-200 mt-3 leading-relaxed">
                Whether you’re working on a thesis, dissertation, or literature review, mastering
                JSTOR’s tools helps you turn research into structured, insightful, and well-supported writing.
              </p>
            </section>

            {/* Key Points */}
            <section id="points" className="bg-white/5 rounded-xl border border-white/10 p-5 sm:p-6">
              <h2 className="text-xl sm:text-2xl font-bold mb-4">5 Key Points</h2>

              <ul className="list-disc pl-5 sm:pl-6 space-y-3 text-gray-200">
                <li>Use advanced filters to find relevant scholarly articles.</li>
                <li>Check author expertise and peer-review status.</li>
                <li>Organize citations using Zotero, EndNote, or Mendeley.</li>
                <li>Use open-access filters for free resources.</li>
                <li>Integrate JSTOR into thesis and literature review workflows.</li>
              </ul>
            </section>

            {/* Value */}
            <section id="value" className="bg-white/5 rounded-xl border border-white/10 p-5 sm:p-6">
              <h2 className="text-xl sm:text-2xl font-bold mb-3">
                Understanding JSTOR and Its Value
              </h2>

              <ul className="list-disc pl-5 sm:pl-6 space-y-3 text-gray-200">
                <li>Peer-reviewed journals ensure strong credibility.</li>
                <li>Historical archives support deep research.</li>
                <li>Filters improve search accuracy.</li>
                <li>Citation tools simplify referencing.</li>
              </ul>
            </section>

            {/* Search */}
            <section id="search" className="bg-white/5 rounded-xl border border-white/10 p-5 sm:p-6">
              <h2 className="text-xl sm:text-2xl font-bold mb-3">
                Crafting Effective Search Strategies
              </h2>

              <p className="text-gray-200 leading-relaxed">
                Use discipline filters, date ranges, and publication types to narrow results.
              </p>

              <pre className="bg-white/10 p-3 rounded text-gray-300 text-xs sm:text-sm mt-3 overflow-x-auto">
"Social justice AND education"
"Digital humanities OR cultural studies"
"Colonial history NOT 20th century"
              </pre>
            </section>

            {/* Credibility */}
            <section id="credibility" className="bg-white/5 rounded-xl border border-white/10 p-5 sm:p-6">
              <h2 className="text-xl sm:text-2xl font-bold mb-3">Evaluating Sources</h2>

              <ul className="list-disc pl-5 sm:pl-6 space-y-3 text-gray-200">
                <li>Check author credentials.</li>
                <li>Prefer reputable peer-reviewed journals.</li>
                <li>Match relevance with your research question.</li>
                <li>Consider publication recency.</li>
              </ul>
            </section>

            {/* Citations */}
            <section id="citations" className="bg-white/5 rounded-xl border border-white/10 p-5 sm:p-6">
              <h2 className="text-xl sm:text-2xl font-bold mb-3">Managing Citations</h2>

              <ul className="list-disc pl-5 sm:pl-6 space-y-3 text-gray-200">
                <li>Export citations in APA, MLA, or Chicago.</li>
                <li>Use reference managers for organization.</li>
                <li>Annotate while reading.</li>
                <li>Keep citation style consistent.</li>
              </ul>
            </section>

            {/* Tools link */}
            <section id="tools" className="bg-white/5 rounded-xl border border-white/10 p-5 sm:p-6">
              <h2 className="text-xl sm:text-2xl font-bold mb-3">JSTOR Tools</h2>

              <ul className="list-disc pl-5 sm:pl-6 space-y-3 text-gray-200">
                <li>Save and organize research with Workspace.</li>
                <li>Use interactive tools for deeper reading.</li>
                <li>Export citations into your managers quickly.</li>
              </ul>

              <p className="text-gray-300 mt-3">
                Also read:{" "}
                <Link to="/blog/research-effectively" className="text-pink-400 underline">
                  How to Research Effectively for Your Assignment
                </Link>
              </p>
            </section>

            {/* Support */}
            <section id="support" className="bg-gradient-to-r from-rose-500/20 to-indigo-500/20 rounded-xl border border-white/10 p-5 sm:p-6">
              <h2 className="text-xl sm:text-2xl font-bold mb-2">How We Help</h2>

              <p className="text-gray-200 leading-relaxed">
                We guide students on JSTOR use, research workflows, referencing,
                and structured academic writing with personalized support.
              </p>
            </section>

            {/* Conclusion */}
            <section id="conclusion" className="bg-white/5 rounded-xl border border-white/10 p-5 sm:p-6">
              <h2 className="text-xl sm:text-2xl font-bold mb-3">Conclusion</h2>

              <p className="text-gray-200 leading-relaxed">
                Learn to search smarter, evaluate critically, and integrate sources
                thoughtfully — and JSTOR becomes a powerful research partner.
              </p>
            </section>
          </article>
        </div>
      </div>
    </section>
  );
}
